<script lang="ts">
  import { Card } from '$lib/components/ui/card';
  import PhilatelicItemRow from './PhilatelicItemRow.svelte';
  import { ItemAndSuggestionListsModel } from './ItemAndSuggestionListsModel.svelte';
  import { ui, overlay } from '$lib/stores/ui';
  import { t } from '$lib/i18n';
  import { user, doLogin } from '$lib/stores/auth';
  import SearchDialog from './SearchDialog.svelte';
  import { typeIndicatorClass, typeButtonClass } from './typeColors';
  import { PhilatelicItemType, canReview } from '@briefmarken_katalog/types';
  import { moveItemToReview } from '$lib/services/philatelic_items';
  import { triggerListUpdate } from '$lib/stores/trigger_list_update';
  import HelpButton from '../HelpButton.svelte';
  import VirtualList from '$lib/components/VirtualList.svelte';
  import { USE_VIRTUAL_LIST } from '../featureFlags';

  const model = new ItemAndSuggestionListsModel();
  const itemSearchOpen = overlay('itemSearch');
  const suggestionSearchOpen = overlay('suggestionSearch');

  async function handleMoveToReview(id: any) {
    try {
      await moveItemToReview(id, doLogin);
      triggerListUpdate();
    } catch (e: any) {
      alert(`Error moving item to review: ${e.message}`);
    }
  }
</script>

<div class="flex flex-col h-full">
<div class="flex flex-1 min-h-0 gap-4">
  <Card class="w-1/2 flex flex-col overflow-hidden card-3d">
    <div class="p-2 bg-gray-100 border-b font-bold card-3d flex items-center gap-1">
      {$t('list.suggestions')}
      {#if model.suggestions !== null}
        <span class="font-normal text-sm text-gray-500">
          ({#if model.ownSuggestionsOnly || model.inReviewByMe || model.suggestionFilter.enabled}{model.visibleSuggestions?.length} / {/if}{model.suggestions.length})
        </span>
      {/if}
      <HelpButton sectionId="main-view" />
    </div>
    <div class="px-2 py-1 bg-gray-50 border-b flex gap-1 min-h-[2rem] items-center">
      <button
        class="px-2 py-0.5 text-xs rounded border transition-colors cursor-pointer
          {model.suggestionFilter.enabled
            ? 'bg-gray-700 text-white border-gray-700'
            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}"
        onclick={() => ui.open('suggestionSearch')}
      >
        {$t('list.search')}
      </button>
      {#if model.canFilterOwn}
        <button
          class="px-2 py-0.5 text-xs rounded border transition-colors cursor-pointer
            {model.ownSuggestionsOnly
              ? 'bg-gray-700 text-white border-gray-700'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}"
          onclick={() => model.ownSuggestionsOnly = !model.ownSuggestionsOnly}
        >
          {$t('list.own')}
        </button>
      {/if}
      {#if model.canFilterInReview}
        <button
          class="px-2 py-0.5 text-xs rounded border transition-colors cursor-pointer
            {model.inReviewByMe
              ? 'bg-gray-700 text-white border-gray-700'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}"
          onclick={() => model.inReviewByMe = !model.inReviewByMe}
        >
          {$t('list.reviewOwned')}
        </button>
      {/if}
      {#if model.canAddSuggestion}
        <div class="ml-auto flex gap-1">
          <button
            class="px-2 py-0.5 text-xs rounded border transition-colors cursor-pointer {typeButtonClass[PhilatelicItemType.SingleStamp]}"
            onclick={() => ui.open('stampEditor')}
          >
            {$t('list.addStamp')}
          </button>
          <button
            class="px-2 py-0.5 text-xs rounded border transition-colors cursor-pointer {typeButtonClass[PhilatelicItemType.Set]}"
            onclick={() => ui.open('setEditor')}
          >
            {$t('list.addSet')}
          </button>
          <button
            class="px-2 py-0.5 text-xs rounded border transition-colors cursor-pointer {typeButtonClass[PhilatelicItemType.Block]}"
            onclick={() => ui.open('blockEditor')}
          >
            {$t('list.addBlock')}
          </button>
          <button
            class="px-2 py-0.5 text-xs rounded border transition-colors cursor-pointer {typeButtonClass[PhilatelicItemType.Booklet]}"
            onclick={() => ui.open('bookletEditor')}
          >
            {$t('list.addBooklet')}
          </button>
          <button
            class="px-2 py-0.5 text-xs rounded border transition-colors cursor-pointer {typeButtonClass[PhilatelicItemType.SeTenants]}"
            onclick={() => ui.open('seTenantsEditor')}
          >
            {$t('list.addSeTenants')}
          </button>
        </div>
      {/if}
    </div>
    <VirtualList items={model.visibleSuggestions ?? []} virtual={USE_VIRTUAL_LIST}>
      {#snippet children(suggestion)}
        <PhilatelicItemRow item={suggestion}/>
      {/snippet}
    </VirtualList>
  </Card>

  <Card class="w-1/2 flex flex-col overflow-hidden card-3d">
    <div class="p-2 bg-gray-100 border-b font-bold card-3d flex items-center gap-1">
      {$t('list.items')}
      {#if model.items !== null}
        <span class="font-normal text-sm text-gray-500">
          ({#if model.ownItemsOnly || model.itemFilter.enabled}{model.visibleItems?.length} / {/if}{model.items.length})
        </span>
      {/if}
      <HelpButton sectionId="main-view" />
    </div>
    <div class="px-2 py-1 bg-gray-50 border-b flex gap-1 min-h-[2rem] items-center">
      <button
        class="px-2 py-0.5 text-xs rounded border transition-colors cursor-pointer
          {model.itemFilter.enabled
            ? 'bg-gray-700 text-white border-gray-700'
            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}"
        onclick={() => ui.open('itemSearch')}
      >
        {$t('list.search')}
      </button>
      {#if model.canFilterOwn}
        <button
          class="px-2 py-0.5 text-xs rounded border transition-colors cursor-pointer
            {model.ownItemsOnly
              ? 'bg-gray-700 text-white border-gray-700'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}"
          onclick={() => model.ownItemsOnly = !model.ownItemsOnly}
        >
          {$t('list.own')}
        </button>
      {/if}
    </div>
    <VirtualList items={model.visibleItems ?? []} virtual={USE_VIRTUAL_LIST}>
      {#snippet children(item)}
        <PhilatelicItemRow
          {item}
          readonly={true}
          onMoveToReview={$user && canReview($user.role) ? () => handleMoveToReview(item._id) : undefined}
        />
      {/snippet}
    </VirtualList>
  </Card>
</div>

<div class="shrink-0 flex flex-wrap gap-x-4 gap-y-1 px-1 py-2 text-xs text-muted-foreground">
  <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm {typeIndicatorClass[PhilatelicItemType.SingleStamp]}"></span>{$t('search.singleStamp')}</span>
  <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm {typeIndicatorClass[PhilatelicItemType.Set]}"></span>{$t('menu.set')}</span>
  <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm {typeIndicatorClass[PhilatelicItemType.Block]}"></span>{$t('menu.block')}</span>
  <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm {typeIndicatorClass[PhilatelicItemType.Booklet]}"></span>{$t('menu.booklet')}</span>
  <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm {typeIndicatorClass[PhilatelicItemType.SeTenants]}"></span>{$t('menu.seTenants')}</span>
</div>
</div>

<SearchDialog
  bind:open={$itemSearchOpen}
  filter={model.itemFilter}
  showOwnOnly={!!$user}
  onreset={() => model.resetItemFilter()}
/>

<SearchDialog
  bind:open={$suggestionSearchOpen}
  filter={model.suggestionFilter}
  showReviewStates={true}
  showOwnOnly={!!$user}
  showReviewedByMe={model.canFilterInReview}
  onreset={() => model.resetSuggestionFilter()}
/>
